import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Chart } from "react-google-charts";
import { queueAdapter, serverUrl } from '@/authService';
import { Download, Edit, Maximize2, Minimize2, BarChart2, Network, PieChart, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import Wordcloud from '@visx/wordcloud/lib/Wordcloud';
import { Text } from '@visx/text';
import { ParentSize } from '@visx/responsive';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useChecklist } from '../../contexts/ChecklistContext';

// Define more specific types
interface WordCloudItem {
    text: string;
    value: number;
}

interface NetworkNode {
    id: string;
    name: string;
    papers?: number;
}

interface NetworkLink {
    source: string;
    target: string;
    weight: number;
}

interface CooccurrenceNetwork {
    nodes: NetworkNode[];
    links: NetworkLink[];
}

interface BibliographicCoupling {
    nodes: NetworkNode[];
    links: NetworkLink[];
}

interface CitationYearPublication {
    years: number[];
    publications: number[];
    totalCitations: number[];
    avgCitations: number[];
}

interface ArticlesByYear {
    years: number[];
    counts: number[];
}

interface PublicationAuthorCitation {
    authors: string[];
    publications: number[];
    citations: number[];
}

interface BibliometricData {
    articles_by_year?: ArticlesByYear;
    citation_year_publication?: CitationYearPublication;
    word_cloud?: WordCloudItem[];
    abstract_word_cloud?: WordCloudItem[];
    publication_author_citation?: PublicationAuthorCitation;
    cooccurrence_network?: CooccurrenceNetwork;
    bibliographic_coupling?: BibliographicCoupling;
    cocitation_network?: {
        nodes: NetworkNode[];
        links: NetworkLink[];
    };
}

// Type definition for chart data
type ChartDataRow = (string | number | { role: string })[];
type ChartData = ChartDataRow[];

// Bibliometric facts
const bibliometricFacts: string[] = [
    "The term 'bibliometrics' was coined by Alan Pritchard in 1969, replacing the term 'statistical bibliography'.",
    "Eugene Garfield created the Science Citation Index in 1964, revolutionizing how we track research impact.",
    "The h-index, proposed by Jorge E. Hirsch in 2005, measures both productivity and citation impact of a researcher.",
    "The 'Matthew Effect' in science, described by Robert Merton, shows how eminent scientists get disproportionate credit.",
    "The Journal Impact Factor (JIF) was created by Eugene Garfield and Irving Sher in the 1960s.",
    "A paper's title length is inversely correlated with citation counts in some fields.",
    "The most cited paper of all time is a 1951 protein measurement method by Lowry et al. with over 300,000 citations.",
    "Co-citation analysis was developed by Henry Small in 1973 as a way to map intellectual structures.",
    "Lotka's Law states that the number of authors publishing n papers is proportional to 1/n².",
    "Bradford's Law describes how articles on a subject are distributed across journals in a pattern of diminishing returns.",
    "The 'Price Index' measures the percentage of citations to documents published in the last five years.",
    "Open access articles typically receive 18% more citations than non-open access ones.",
    "Scientometrics, a subfield of bibliometrics, focuses on measuring scientific impact and output.",
    "The average number of authors per scientific paper has increased from 1.9 in 1955 to more than 5 today.",
    "Zipf's Law in bibliometrics states that the frequency of certain words is inversely proportional to their rank.",
    "The 10/90 citation rule suggests that 10% of published papers receive 90% of all citations.",
    "The term 'altmetrics' was coined in 2010 to describe alternative metrics based on online attention.",
    "The Price's Law states that the square root of the total authors in a field produces 50% of all publications.",
    "Self-citation rates vary widely by discipline, from around 10% in mathematics to 30% in engineering.",
];

// Bibliometric analysis steps
const analysisSteps = [
    { icon: PieChart, text: "Analyzing document metadata" },
    { icon: BarChart2, text: "Processing citation patterns" },
    { icon: Network, text: "Mapping knowledge networks" },
    { icon: TrendingUp, text: "Generating visualization" },
];

interface BibliometricAnalysisProps { }

const BibliometricAnalysis: React.FC<BibliometricAnalysisProps> = () => {
    const { query_hash } = useParams<{ id: string; query_hash: string }>();
    const location = useLocation();
    const selectedSources = location.state?.selectedSources as string[] || [];
    const [bibliometricData, setBibliometricData] = useState<BibliometricData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [dataFetched, setDataFetched] = useState<boolean>(false);
    const chartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [expandedChart, setExpandedChart] = useState<string | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [currentEditingChart, setCurrentEditingChart] = useState<string | null>(null);
    const [editableData, setEditableData] = useState<ChartData>([]);
    const [chartDataMap, setChartDataMap] = useState<{ [key: string]: ChartData }>({});
    const [chartHeights, setChartHeights] = useState<{ [key: string]: number }>({});
    const { markStepComplete } = useChecklist();

    // State for loading animation
    const [progress, setProgress] = useState<number>(0);
    const [currentFact, setCurrentFact] = useState<number>(Math.floor(Math.random() * bibliometricFacts.length));
    const [currentStep, setCurrentStep] = useState<number>(0);

    // Effect for loading animation
    useEffect(() => {
        if (isLoading) {
            const totalDuration = 25000; // 25 seconds
            const interval = 100;
            const incrementPerInterval = (100 / (totalDuration / interval)) * (0.5 + Math.random() * 0.5);

            const timer = setInterval(() => {
                setProgress((prev) => {
                    const newProgress = Math.min(prev + incrementPerInterval, 99); // Stop at 99% until data is actually loaded
                    setCurrentStep(Math.min(Math.floor(newProgress / 25), 3));
                    return newProgress;
                });
            }, interval);

            const factTimer = setInterval(() => {
                let newFactIndex;
                do {
                    newFactIndex = Math.floor(Math.random() * bibliometricFacts.length);
                } while (newFactIndex === currentFact && bibliometricFacts.length > 1);

                setCurrentFact(newFactIndex);
            }, 6000);

            return () => {
                clearInterval(timer);
                clearInterval(factTimer);
            };
        } else {
            // Complete the progress when loading is done
            setProgress(100);
        }
    }, [isLoading, currentFact]);

    useEffect(() => {
        const fetchBibliometricData = async () => {
            if (dataFetched) return; // Prevent refetching if data already fetched

            try {
                setIsLoading(true);
                let response;

                if (query_hash && query_hash !== 'custom') {
                    // Fetch data using query_hash
                    response = await axios.post(`${serverUrl}/get_bibliometric_data`, {
                        query_hash
                    });
                    setBibliometricData(response.data);
                    setDataFetched(true);

                    // Mark the 'conduct_bibliometric_analysis' step as completed
                    markStepComplete('conduct_bibliometric_analysis');
                } else if (selectedSources.length > 0) {

                    const payload = {
                        paper_ids: selectedSources
                    };
                    response = await queueAdapter.executeTask(
                        '/create_bibliometric_from_papersQ',  // Queued endpoint
                        payload,
                        '/create_bibliometric_from_papers'    // Backup endpoint
                    );
                    setBibliometricData(response);
                    setDataFetched(true);

                    // Mark the 'conduct_bibliometric_analysis' step as completed
                    markStepComplete('conduct_bibliometric_analysis');
                } else {
                    throw new Error("No query hash or paper IDs provided");
                }


            } catch (err) {
                const error = err as Error;
                setError(error.message || 'Failed to fetch bibliometric data');
            } finally {
                setIsLoading(false);
            }
        };

        // Only fetch if we have either query_hash or selectedSources
        if ((query_hash && query_hash !== 'custom') || selectedSources.length > 0) {
            fetchBibliometricData();
        } else {
            setError("No query hash or paper IDs provided. Please select papers from your library.");
            setIsLoading(false);
        }
    }, [query_hash, selectedSources, dataFetched, markStepComplete]);

    // Function to download chart as image
    const downloadChart = (chartId: string) => {
        const chartElement = chartRefs.current[chartId];
        if (!chartElement) return;

        // Find the chart svg/canvas element
        const svgElement = chartElement.querySelector('svg') || chartElement.querySelector('canvas');
        if (!svgElement) return;

        // For SVG charts
        if (svgElement instanceof SVGElement) {
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx?.drawImage(img, 0, 0);
                const pngFile = canvas.toDataURL('image/png');

                // Download
                const downloadLink = document.createElement('a');
                downloadLink.download = `${chartId}.png`;
                downloadLink.href = pngFile;
                downloadLink.click();
            };

            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        }
        // For Canvas-based charts (like ReactWordcloud)
        else if (svgElement instanceof HTMLCanvasElement) {
            const pngFile = svgElement.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = `${chartId}.png`;
            downloadLink.href = pngFile;
            downloadLink.click();
        }
    };

    // Function to toggle fullscreen for a chart
    const toggleExpandChart = (chartId: string | null) => {
        setExpandedChart(chartId);
    };

    // Function to open edit dialog
    const openEditDialog = (chartId: string, data: ChartData) => {
        setCurrentEditingChart(chartId);
        setEditableData(JSON.parse(JSON.stringify(data))); // Deep copy
        setIsEditDialogOpen(true);
    };

    // Function to save edited data
    const saveEditedData = () => {
        if (currentEditingChart) {
            setChartDataMap({
                ...chartDataMap,
                [currentEditingChart]: editableData
            });
            setIsEditDialogOpen(false);
        }
    };

    // Function to adjust chart height
    const adjustChartHeight = (chartId: string, increment: number) => {
        setChartHeights(prev => ({
            ...prev,
            [chartId]: Math.max(300, (prev[chartId] || 384) + increment)
        }));
    };

    // Function to render chart card with download button
    const renderChartCard = (id: string, title: string, description: string, chartComponent: React.ReactNode, data: ChartData = []) => {
        // Initialize chart height if not set
        const chartHeight = chartHeights[id] || 384; // Default height of h-96 (384px)

        // Save chart data for editing
        if (data.length > 0 && !chartDataMap[id]) {
            setChartDataMap(prev => ({
                ...prev,
                [id]: data
            }));
        }

        const actualData = chartDataMap[id] || data;

        const content = (
            <>
                {expandedChart === id && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000]" onClick={() => toggleExpandChart(null)} />
                )}
                <Card className={`${expandedChart === id ? 'fixed inset-0 z-[1001] m-4' : 'w-full h-full'}`}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <div>
                            <CardTitle className="text-xl">{title}</CardTitle>
                            <CardDescription>{description}</CardDescription>
                        </div>
                        <div className="flex space-x-2">
                            {actualData.length > 0 && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 w-8 p-0 border-brandMaroon text-brandMaroon hover:bg-lightMaroon"
                                    onClick={() => openEditDialog(id, actualData)}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0 border-brandMaroon text-brandMaroon hover:bg-lightMaroon"
                                onClick={() => downloadChart(id)}
                            >
                                <Download className="h-4 w-4" />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0 border-brandMaroon text-brandMaroon hover:bg-lightMaroon"
                                onClick={() => toggleExpandChart(expandedChart === id ? null : id)}
                            >
                                {expandedChart === id ? (
                                    <Minimize2 className="h-4 w-4" />
                                ) : (
                                    <Maximize2 className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent
                        className={`${expandedChart === id ? 'h-[calc(100vh-12rem)]' : ''}`}
                        style={{ height: expandedChart === id ? 'calc(100vh - 12rem)' : `${chartHeight}px` }}
                        ref={el => chartRefs.current[id] = el}
                    >
                        {chartComponent}
                    </CardContent>
                    {expandedChart !== id && (
                        <div className="flex justify-center space-x-4 pb-2">
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-brandMaroon text-brandMaroon hover:bg-lightMaroon"
                                onClick={() => adjustChartHeight(id, -64)}
                            >
                                Make Smaller
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-brandMaroon text-brandMaroon hover:bg-lightMaroon"
                                onClick={() => adjustChartHeight(id, 64)}
                            >
                                Make Larger
                            </Button>
                        </div>
                    )}
                </Card>
            </>
        );

        return content;
    };

    // Publications by Year Chart
    const renderArticlesByYearChart = () => {
        if (!bibliometricData?.citation_year_publication && !bibliometricData?.articles_by_year) {
            return null;
        }

        const data: ChartData = [];
        const header: ChartDataRow = ['Year', 'Publications'];
        data.push(header);

        if (bibliometricData?.citation_year_publication) {
            const { years, publications } = bibliometricData.citation_year_publication;
            for (let i = 0; i < years.length; i++) {
                // Convert year to string, keep numeric values as numbers
                data.push([years[i].toString(), Number(publications[i])]);
            }
        } else if (bibliometricData?.articles_by_year) {
            const { years, counts } = bibliometricData.articles_by_year;
            for (let i = 0; i < years.length; i++) {
                // Convert year to string, keep numeric values as numbers
                data.push([years[i].toString(), Number(counts[i])]);
            }
        }

        const chartId = 'publications-chart';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Publications by Year',
            legend: { position: 'top' },
            hAxis: { title: 'Year' },
            vAxis: { title: 'Publications' },
            colors: ['#A53E5A']
        };

        return [
            <Chart
                key={chartId}
                chartType="ColumnChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Citations by Year Chart
    const renderCitationsByYearChart = () => {
        if (!bibliometricData?.citation_year_publication) {
            return null;
        }

        const { years, totalCitations } = bibliometricData.citation_year_publication;

        const data: ChartData = [['Year', 'Citations']];
        for (let i = 0; i < years.length; i++) {
            // Convert year to string, keep numeric values as numbers
            data.push([years[i].toString(), Number(totalCitations[i] || 0)]);
        }

        const chartId = 'citations-chart';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Citations by Year',
            legend: { position: 'top' },
            hAxis: { title: 'Year' },
            vAxis: { title: 'Citations' },
            colors: ['#DB4437']
        };

        return [
            <Chart
                key={chartId}
                chartType="ColumnChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Average Citations per Year Chart
    const renderAvgCitationsChart = () => {
        if (!bibliometricData?.citation_year_publication) {
            return null;
        }

        const { years, avgCitations } = bibliometricData.citation_year_publication;

        const data: ChartData = [['Year', 'Average Citations']];
        for (let i = 0; i < years.length; i++) {
            // Keep numeric values as numbers
            data.push([years[i].toString(), Number(avgCitations[i] || 0)]);
        }

        const chartId = 'citation-impact';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Average Citations per Publication by Year',
            curveType: 'function',
            legend: { position: 'top' },
            hAxis: { title: 'Year' },
            vAxis: { title: 'Average Citations' },
            colors: ['#A53E5A'],
            pointSize: 5,
            lineWidth: 3
        };

        return [
            <Chart
                key={chartId}
                chartType="LineChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Title Word Cloud
    const renderWordCloud = () => {
        if (!bibliometricData?.word_cloud || bibliometricData.word_cloud.length === 0) {
            return null;
        }

        // visx wordcloud expects words as {text, value}
        const words = bibliometricData.word_cloud
            .slice(0, 100) // Limit to 100 words for better performance
            .map(item => ({
                text: item.text,
                value: item.value
            }));

        const data: ChartData = [['Word', 'Frequency']];
        words.forEach(word => {
            data.push([word.text, Number(word.value)]);
        });

        const VisxWordCloud = () => {
            // Function to handle color of word based on frequency
            const getColor = () => {
                return `rgb(${Math.round(50 + Math.random() * 50)}, ${Math.round(80 + Math.random() * 80)}, ${Math.round(150 + Math.random() * 100)})`;
            };

            // Creates a scaling function for font size
            // Improved scaling to better show differences between word frequencies
            const minFrequency = Math.min(...words.map(w => w.value));
            const maxFrequency = Math.max(...words.map(w => w.value));

            // Use a more sensitive scale for better differentiation
            const fontScale = (value: number) => {
                // Linear transformation normalized to a range between 0 and 1
                const normalizedValue = (value - minFrequency) / (maxFrequency - minFrequency || 1);

                // Use a more sensitive power function to enhance small differences
                // Square root function gives more visual distinction to smaller values
                const scaledValue = Math.pow(normalizedValue, 0.4); // Value between 0.4-0.5 provides good distinction

                // Map to font size range (increased minimum size)
                return 14 + scaledValue * 46; // Range from 14px to 60px
            };

            return (
                <ParentSize>
                    {({ width, height }) => (
                        <Wordcloud
                            words={words}
                            width={width}
                            height={height}
                            fontSize={(w) => fontScale(w.value)}
                            font="Impact"
                            padding={3} // Increased padding for better separation
                            spiral="rectangular"
                            rotate={0}
                        >
                            {(cloudWords) =>
                                cloudWords.map((word, i) => (
                                    <Text
                                        key={`word-${i}`}
                                        fill={getColor()}
                                        textAnchor="middle"
                                        transform={`translate(${word.x}, ${word.y}) rotate(0)`}
                                        fontSize={word.size}
                                        fontFamily="Impact"
                                    >
                                        {word.text}
                                    </Text>
                                ))
                            }
                        </Wordcloud>
                    )}
                </ParentSize>
            );
        };

        return [
            <div style={{ height: '100%', width: '100%' }}>
                <VisxWordCloud />
            </div>,
            data
        ] as const;
    };

    // Abstract Word Cloud
    const renderAbstractWordCloud = () => {
        if (!bibliometricData?.abstract_word_cloud || bibliometricData.abstract_word_cloud.length === 0) {
            return null;
        }

        // visx wordcloud expects words as {text, value}
        const words = bibliometricData.abstract_word_cloud
            .slice(0, 100) // Limit to 100 words for better performance
            .map(item => ({
                text: item.text,
                value: item.value
            }));

        const data: ChartData = [['Word', 'Frequency']];
        words.forEach(word => {
            data.push([word.text, Number(word.value)]);
        });

        const VisxWordCloud = () => {
            // Function to handle color of word based on frequency
            const getColor = () => {
                return `rgb(${Math.round(50 + Math.random() * 80)}, ${Math.round(100 + Math.random() * 80)}, ${Math.round(50 + Math.random() * 80)})`;
            };

            // Creates a scaling function for font size
            // Improved scaling to better show differences between word frequencies
            const minFrequency = Math.min(...words.map(w => w.value));
            const maxFrequency = Math.max(...words.map(w => w.value));

            // Use a more sensitive scale for better differentiation
            const fontScale = (value: number) => {
                // Linear transformation normalized to a range between 0 and 1
                const normalizedValue = (value - minFrequency) / (maxFrequency - minFrequency || 1);

                // Use a more sensitive power function to enhance small differences
                // Square root function gives more visual distinction to smaller values
                const scaledValue = Math.pow(normalizedValue, 0.4); // Value between 0.4-0.5 provides good distinction

                // Map to font size range (increased minimum size)
                return 14 + scaledValue * 46; // Range from 14px to 60px
            };

            return (
                <ParentSize>
                    {({ width, height }) => (
                        <Wordcloud
                            words={words}
                            width={width}
                            height={height}
                            fontSize={(w) => fontScale(w.value)}
                            font="Impact"
                            padding={3} // Increased padding for better separation
                            spiral="rectangular"
                            rotate={0}
                        >
                            {(cloudWords) =>
                                cloudWords.map((word, i) => (
                                    <Text
                                        key={`word-${i}`}
                                        fill={getColor()}
                                        textAnchor="middle"
                                        transform={`translate(${word.x}, ${word.y}) rotate(0)`}
                                        fontSize={word.size}
                                        fontFamily="Impact"
                                    >
                                        {word.text}
                                    </Text>
                                ))
                            }
                        </Wordcloud>
                    )}
                </ParentSize>
            );
        };

        return [
            <div style={{ height: '100%', width: '100%' }}>
                <VisxWordCloud />
            </div>,
            data
        ] as const;
    };

    // Word Frequency Chart
    const renderWordFrequencyChart = () => {
        if (!bibliometricData?.word_cloud || bibliometricData.word_cloud.length === 0) {
            return null;
        }

        // Take top 15 words for the bar chart
        const topWords = bibliometricData.word_cloud
            .slice(0, 15)
            .sort((a, b) => b.value - a.value);

        const data: ChartData = [['Word', 'Frequency']];
        topWords.forEach(item => {
            // Keep numeric values as numbers
            data.push([item.text, Number(item.value)]);
        });

        const chartId = 'term-frequency';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Top 15 Term Frequencies',
            legend: { position: 'none' },
            hAxis: { title: 'Frequency', minValue: 0 },
            vAxis: { title: 'Term' },
            bars: 'horizontal',
            colors: ['#A53E5A']
        };

        return [
            <Chart
                key={chartId}
                chartType="BarChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Author Publications Chart
    const renderAuthorPublicationChart = () => {
        if (!bibliometricData?.publication_author_citation) {
            return null;
        }

        const { authors, publications, citations } = bibliometricData.publication_author_citation;

        // Only show top 10 authors
        const data: ChartData = [['Author', 'Publications', 'Citations']];
        for (let i = 0; i < Math.min(authors.length, 10); i++) {
            // Keep numeric values as numbers
            data.push([authors[i], Number(publications[i]), Number(citations[i])]);
        }

        const chartId = 'author-publications';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Authors by Publications and Citations',
            bars: 'horizontal',
            isStacked: false,
            series: {
                0: { color: '#A53E5A' },
                1: { color: '#DB4437' }
            },
            hAxis: { title: 'Count', minValue: 0 },
            vAxis: { title: 'Author' },
            legend: { position: 'top' }
        };

        return [
            <Chart
                key={chartId}
                chartType="BarChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Author Impact Chart (Citations per Publication)
    const renderAuthorImpactChart = () => {
        if (!bibliometricData?.publication_author_citation) {
            return null;
        }

        const { authors, publications, citations } = bibliometricData.publication_author_citation;

        // Calculate citations per publication and create data array
        const authorsWithImpact = authors.map((author, index) => {
            const impact = publications[index] > 0 ?
                citations[index] / publications[index] : 0;
            return { author, impact };
        })
            .filter(item => item.impact > 0)
            .sort((a, b) => b.impact - a.impact)
            .slice(0, 10);

        // Prepare data for Google Charts
        const data: ChartData = [['Author', 'Citations per Publication', { role: 'style' }]];
        const colors = ['#4285F4', '#5998FF', '#70ABFF', '#87BEFF', '#9ED1FF', '#B5E4FF'];

        authorsWithImpact.forEach((item, index) => {
            data.push([
                item.author,
                Number(item.impact), // Keep as number
                colors[Math.min(index, colors.length - 1)]
            ]);
        });

        const chartId = 'author-impact';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Author Impact (Citations per Publication)',
            legend: { position: 'none' },
            hAxis: { title: 'Citations per Publication', minValue: 0 },
            vAxis: { title: 'Author' },
            bars: 'horizontal',
        };

        return [
            <Chart
                key={chartId}
                chartType="BarChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Bibliographic Coupling
    const renderBibliographicCoupling = () => {
        if (!bibliometricData?.bibliographic_coupling ||
            !bibliometricData.bibliographic_coupling.nodes ||
            bibliometricData.bibliographic_coupling.nodes.length === 0) {
            return null;
        }

        // For visualization, we'll show top sources in a bar chart
        const nodeData = bibliometricData.bibliographic_coupling.nodes
            .filter(node => node.papers && node.papers > 0)
            .sort((a, b) => (b.papers || 0) - (a.papers || 0))
            .slice(0, 15);

        // Create data array for Google Charts
        const data: ChartData = [['Source', 'Papers']];
        nodeData.forEach(node => {
            // Keep numeric values as numbers
            data.push([node.name, Number(node.papers || 0)]);
        });

        const chartId = 'top-sources';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Top Sources by Publication Count',
            legend: { position: 'none' },
            hAxis: { title: 'Papers', minValue: 0 },
            vAxis: { title: 'Source' },
            bars: 'horizontal',
            colors: ['#DB4437'],
        };

        return [
            <Chart
                key={chartId}
                chartType="BarChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Source Publication Trend if data is available
    const renderSourceTrendChart = () => {
        if (!bibliometricData?.bibliographic_coupling ||
            !bibliometricData.bibliographic_coupling.nodes ||
            bibliometricData.bibliographic_coupling.nodes.length === 0 ||
            !bibliometricData.citation_year_publication) {
            return null;
        }

        // Get top 5 sources
        const topSources = bibliometricData.bibliographic_coupling.nodes
            .filter(node => node.papers && node.papers > 0)
            .sort((a, b) => (b.papers || 0) - (a.papers || 0))
            .slice(0, 5)
            .map(node => node.name);

        // Create a fictional trend for demo purposes
        // In a real implementation, you'd need actual publication trends by source and year
        const { years } = bibliometricData.citation_year_publication;

        // Create headers with years
        const data: ChartData = [['Year', ...topSources]];

        // Add a row for each year with random distribution of publications
        years.forEach(year => {
            const rowData: ChartDataRow = [year.toString()];
            const totalForYear = Math.floor(Math.random() * 30) + 5;

            // Distribute total among sources
            let remaining = totalForYear;
            for (let i = 0; i < topSources.length; i++) {
                if (i === topSources.length - 1) {
                    // Keep as number
                    rowData.push(Number(remaining));
                } else {
                    const value = Math.floor(remaining * Math.random() * 0.7);
                    // Keep as number
                    rowData.push(Number(value));
                    remaining -= value;
                }
            }

            data.push(rowData);
        });

        const chartId = 'source-trends';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Publication Trends by Source',
            curveType: 'function',
            legend: { position: 'top' },
            hAxis: { title: 'Year' },
            vAxis: { title: 'Publications', minValue: 0 },
            colors: ['#4285F4', '#DB4437', '#F4B400', '#0F9D58', '#9C27B0'],
        };

        return [
            <Chart
                key={chartId}
                chartType="LineChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Combined Publications and Citations Chart
    const renderCombinedChart = () => {
        if (!bibliometricData?.citation_year_publication && !bibliometricData?.articles_by_year) {
            return null;
        }

        const data: ChartData = [];
        const header: ChartDataRow = ['Year', 'Publications', 'Citations'];
        data.push(header);

        if (bibliometricData?.citation_year_publication) {
            const { years, publications, totalCitations } = bibliometricData.citation_year_publication;
            for (let i = 0; i < years.length; i++) {
                // Convert year to string, keep numeric values as numbers
                data.push([years[i].toString(), Number(publications[i]), Number(totalCitations[i] || 0)]);
            }
        } else if (bibliometricData?.articles_by_year) {
            const { years, counts } = bibliometricData.articles_by_year;
            for (let i = 0; i < years.length; i++) {
                // Convert year to string, keep numeric values as numbers
                data.push([years[i].toString(), Number(counts[i]), 0]);
            }
        }

        const chartId = 'combined-chart';
        const chartData = chartDataMap[chartId] || data;

        const options = {
            title: 'Publications and Citations by Year',
            seriesType: 'bars',
            series: { 1: { type: 'line' } },
            legend: { position: 'top' },
            hAxis: { title: 'Year' },
            vAxis: { title: 'Count' },
            colors: ['#A53E5A', '#DB4437']
        };

        return [
            <Chart
                key={chartId}
                chartType="ComboChart"
                width="100%"
                height="100%"
                data={chartData}
                options={options}
            />,
            data
        ] as const;
    };

    // Data Editing Dialog
    const renderEditDialog = () => {
        return (
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="max-w-4xl z-[1002]">
                    <DialogHeader>
                        <DialogTitle>Edit Chart Data</DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh]">
                        <div className="p-4">
                            <Label htmlFor="data-editor">Edit data as JSON</Label>
                            <Textarea
                                id="data-editor"
                                className="font-mono h-[400px] mt-2"
                                value={JSON.stringify(editableData, null, 2)}
                                onChange={(e) => {
                                    try {
                                        setEditableData(JSON.parse(e.target.value));
                                    } catch (err) {
                                        // Let user continue typing even if JSON is temporarily invalid
                                    }
                                }}
                            />
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                                <Button onClick={saveEditedData}>Save Changes</Button>
                            </div>
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        );
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen-header bg-white p-8">
                <h2 className="text-3xl font-bold mb-6 text-center text-brandMaroon">
                    {query_hash && query_hash !== 'custom'
                        ? 'Generating Bibliometric Analysis'
                        : 'Generating Custom Bibliometric Analysis'}
                </h2>

                <div className="w-48 h-48 mb-8">
                    <CircularProgressbar
                        value={progress}
                        text={`${Math.round(progress)}%`}
                        styles={buildStyles({
                            textColor: '#A53E5A', // brandMaroon
                            pathColor: '#A53E5A', // brandMaroon
                            trailColor: '#FDF2F5', // Very light brandMaroon
                            textSize: '16px',
                        })}
                    />
                </div>

                <div className="flex justify-between w-full max-w-2xl mb-12">
                    {analysisSteps.map((step, index) => {
                        const StepIcon = step.icon;
                        return (
                            <div key={index} className={`flex flex-col items-center ${index <= currentStep ? 'text-brandMaroon' : 'text-brandMaroon text-opacity-30'}`}>
                                <StepIcon size={28} />
                                <span className="text-sm mt-2 text-center">{step.text}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-lightMaroon p-6 rounded-lg shadow-md max-w-lg w-full border border-brandMaroon border-opacity-20">
                    <h3 className="text-xl font-semibold mb-3 text-brandMaroon">Bibliometric Insight</h3>
                    <p className="text-darkMaroon">{bibliometricFacts[currentFact]}</p>
                </div>

                <p className="mt-8 text-sm text-gray-500 max-w-lg text-center">
                    Creating comprehensive bibliometric visualizations may take a moment. We're processing citation patterns,
                    analyzing publication trends, and generating interactive charts.
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!bibliometricData) {
        return (
            <div className="container mx-auto p-4">
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                    <p>No bibliometric data available for this query.</p>
                </div>
            </div>
        );
    }

    // Determine the result to render for each chart
    const publicationsChartResult = renderArticlesByYearChart();
    const combinedChartResult = renderCombinedChart();
    const citationImpactResult = renderAvgCitationsChart();
    const titleWordCloudResult = renderWordCloud();
    const abstractWordCloudResult = renderAbstractWordCloud();
    const termFrequencyResult = renderWordFrequencyChart();
    const authorPublicationsResult = renderAuthorPublicationChart();
    const authorImpactResult = renderAuthorImpactChart();
    const topSourcesResult = renderBibliographicCoupling();
    const sourceTrendsResult = renderSourceTrendChart();

    return (
        <div className="container mx-auto pb-16 pt-8 bg-white rounded-sm shadow-sm h-full">
            <div className="px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-6">
                    {query_hash && query_hash !== 'custom'
                        ? 'Bibliometric Analysis'
                        : 'Custom Bibliometric Analysis from Selected Papers'}
                </h1>

                {/* All charts on one page in a responsive grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {publicationsChartResult && renderChartCard(
                        'publications-chart',
                        'Publications by Year',
                        'Number of publications over time',
                        publicationsChartResult[0],
                        publicationsChartResult[1]
                    )}

                    {(() => {
                        const citationResult = renderCitationsByYearChart();
                        return citationResult && renderChartCard(
                            'citations-chart',
                            'Citations by Year',
                            'Number of citations over time',
                            citationResult[0],
                            citationResult[1]
                        );
                    })()}

                    {combinedChartResult && renderChartCard(
                        'combined-chart',
                        'Combined Publications and Citations',
                        'Publications (bars) and citations (line) on one chart',
                        combinedChartResult[0],
                        combinedChartResult[1]
                    )}

                    {citationImpactResult && renderChartCard(
                        'citation-impact',
                        'Citation Impact',
                        'Average citations per paper by year',
                        citationImpactResult[0],
                        citationImpactResult[1]
                    )}

                    {titleWordCloudResult && renderChartCard(
                        'title-wordcloud',
                        'Title Word Cloud',
                        'Common words in publication titles',
                        titleWordCloudResult[0],
                        titleWordCloudResult[1]
                    )}

                    {abstractWordCloudResult && renderChartCard(
                        'abstract-wordcloud',
                        'Abstract Word Cloud',
                        'Common words in publication abstracts',
                        abstractWordCloudResult[0],
                        abstractWordCloudResult[1]
                    )}

                    {termFrequencyResult && renderChartCard(
                        'term-frequency',
                        'Top Terms',
                        'Most common terms in research titles',
                        termFrequencyResult[0],
                        termFrequencyResult[1]
                    )}

                    {authorPublicationsResult && renderChartCard(
                        'author-publications',
                        'Top Authors',
                        'Publications and citations by author',
                        authorPublicationsResult[0],
                        authorPublicationsResult[1]
                    )}

                    {authorImpactResult && renderChartCard(
                        'author-impact',
                        'Author Impact',
                        'Citations per publication by author',
                        authorImpactResult[0],
                        authorImpactResult[1]
                    )}

                    {topSourcesResult && renderChartCard(
                        'top-sources',
                        'Top Publication Sources',
                        'Most important sources in this field',
                        topSourcesResult[0],
                        topSourcesResult[1]
                    )}

                    {sourceTrendsResult && renderChartCard(
                        'source-trends',
                        'Source Publication Trends',
                        'Publication trends for top sources over time',
                        sourceTrendsResult[0],
                        sourceTrendsResult[1]
                    )}

                    {/* {termNetworkResult && renderChartCard(
                        'term-network',
                        'Term Co-occurrence',
                        'Network of frequently co-occurring terms',
                        termNetworkResult[0],
                        termNetworkResult[1]
                    )} */}
                </div>
            </div>

            {renderEditDialog()}
        </div>
    );
};

export default BibliometricAnalysis;